<script setup lang="ts">
import { useJoinStore, useNationStore } from "@/store";
import { useFormBuilder } from "@/services";
import { localize } from "@/utils";
import { vMaska } from "maska";
import { computed, onMounted, reactive, ref, watch } from "vue";
import router from "@/router";
import { postPlannerSignUp } from "@/api/signup/postPlannerSignUp";
import { AttachementFileType } from "@/api/type";
import { FileTransferService, FileTransferSource, FileUploadSource } from "@/libs/R2ServiceClient";

const { member } = useJoinStore();
const isKorean = member.country.code == "KR";

const { getPhone } = useNationStore();
const phoneCode = computed(() => getPhone(member.country.code));

const { validate, invalid } = useFormBuilder();

const form = reactive({
  phone: "",
  plannerName: "", // 플래너명
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  plannerQualificationDocuments: [] as AttachementFileType[],
});

const errors = ref<Record<string, boolean>>({});
const submitted = ref(false);

onMounted(init);

/**
 * 초기화
 */
function init() {
  validate();
}

const validateForm = () => {
  const newErrors: Record<string, boolean> = {};
  if (!form.phone.trim()) {
    newErrors.phone = true;
  }
  if (!form.plannerName.trim()) {
    newErrors.plannerName = true;
  }
  if (!form.postalCode.trim()) {
    newErrors.postalCode = true;
  }
  if (!form.address1.trim()) {
    newErrors.address1 = true;
  }
  if (!form.address2.trim()) {
    newErrors.address2 = true;
  }
  if (form.plannerQualificationDocuments.length === 0) {
    newErrors.plannerQualificationDocuments = true;
  }

  // 해외만 필수
  if (!isKorean) {
    if (!form.city.trim()) {
      newErrors.city = true;
    }

    if (!form.state.trim()) {
      newErrors.state = true;
    }
  }
  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};

/** 파일 업로드 함수 */
const fileInputRef = ref<HTMLInputElement | null>(null);

const uploadFiles = async (files: File[]) => {
  if (!files.length) return;

  const uploadSource = new FileUploadSource("USER_UPLOAD");
  files.forEach((file) => uploadSource.addBrowserData(file));
  const transferSource = new FileTransferSource(uploadSource);
  const fileService = new FileTransferService();

  const uploadResult = await fileService.transfer(transferSource);
  if (!uploadResult.upload) return;

  const existingFiles = form.plannerQualificationDocuments || [];
  const startIndex = existingFiles.length; // 기존 개수 기준으로 인덱스 시작점 지정

  const convertedFiles: AttachementFileType[] = uploadResult.upload.map(
    (file: any, i: number) => ({
      id: file.fileId,
      name: file.fileName,
      type: file.uri.split(".").pop() || "",
      size: file.fileSize,
      index: startIndex + i,
    })
  );

  // 병합
  form.plannerQualificationDocuments = [
    ...existingFiles,
    ...convertedFiles,
  ];

  if (fileInputRef.value) fileInputRef.value.value = "";
};

const onFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const files = target.files;
  if (!files || files.length === 0) return;

  const newFiles = Array.from(files);
  const existingFiles = form.plannerQualificationDocuments || [];
  const originLength = existingFiles.length;

  if (originLength + newFiles.length > 5) {
    alert(localize("최대 5개의 파일만 업로드할 수 있습니다."));
    target.value = "";
    return;
  }

  // 확장자 제한
  const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf"];
  const invalidExtFiles = newFiles.filter(
    (file) =>
      !allowedExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      )
  );
  if (invalidExtFiles.length > 0) {
    alert(localize("JPG, JPEG, PNG, PDF 파일만 업로드할 수 있습니다."));
    target.value = "";
    return;
  }

  // 파일 크기 제한 (40MB 이하)
  const maxSize = 40 * 1024 * 1024;
  const oversizedFiles = newFiles.filter((file) => file.size > maxSize);
  if (oversizedFiles.length > 0) {
    alert(localize("각 파일은 40MB 이하만 업로드할 수 있습니다."));
    target.value = "";
    return;
  }

  uploadFiles(newFiles);
};

const done = async() => {
  submitted.value = true;
  if (!validateForm()) {
    return;
  }
  if (
    !form.plannerQualificationDocuments ||
    form.plannerQualificationDocuments.length === 0
  ) {
    alert(localize("요청 본문이 누락되었거나 형식이 올바르지 않습니다."));
    return;
  }
  try {
    const res = await postPlannerSignUp({
      verificationRequestId: member.verificationRequestId,
      loginId: member.loginId,
      password: member.password,
      firstName: member.name.firstName,
      lastName: member.name.lastName,
      birthDate: member.birthDate,
      gender: member.sex,
      countryCode: member.country.code,
      contactCountryNumber: member.phoneNumber.countryCode,
      contactNumber: member.phoneNumber.number,
      agreementId: member.agreement.id,
      agreementVersion: member.agreement.version,
      plannerName: form.plannerName,
      plannerContactNumber: form.phone,
      plannerAddress: {
        postalCode: form.postalCode,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state: form.state
      },
      plannerQualificationDocuments: form.plannerQualificationDocuments,
    });
    if(res.isSuccess) {
      router.push("/home/join/complete/planners");
    } else {
      if(res.error.code === "E_VALIDATION") {
        alert(localize("입력값이 올바르지 않습니다."));
        return;
      }
      if(res.error.code === "E_MEMBEERSHIP_001") {
        alert(localize("이미 존재하는 로그인 ID입니다."));
        return;
      }
      if(res.error.code === "E_VERIFICATION_001") {
        alert(localize("인증 요청을 찾을 수 없습니다."));
        return; 
      }
      if(res.error.code === "E_BODY_001"){
        alert(localize("요청 본문이 누락되었거나 형식이 올바르지 않습니다."))
      }
    }
  } catch (err) {
    alert(localize('서버 연결에 실패하였습니다.<br/>잠시후, 다시 시도해 주세요.'));
  }
}

const removeUploadedFile = (fileId: string) => {
  const existingFiles: AttachementFileType[] =
    form.plannerQualificationDocuments || [];

  form.plannerQualificationDocuments = existingFiles
    .filter((file) => file.id !== fileId)
    .map((file, index) => ({
      ...file,
      index,
    }));
};


const onClickUpload = () => {
  fileInputRef.value?.click();
};

const formatFileSize = (size: number) => {
  const mb = size / (1024 * 1024);

  if (mb >= 1) {
    return `${mb.toFixed(1)}MB`;
  }

  const kb = size / 1024;
  return `${kb.toFixed(1)}KB`;
};

watch(
  form,
  () => submitted.value && validateForm(),
  { deep: true }
);

</script>

<template>
  <div class="join-box">
    <div class="progress-line">
      <div class="line" style="width: 80%"></div>
    </div>
    <div class="step-wrap">
      <b>STEP 4</b>
      <h2>{{ localize("R2 플래너 정보 입력") }}</h2>
    </div>
    <div class="email">{{ member.loginId }}</div>
    <form @submit.prevent="done" class="join-form">
      <div class="form-group">
        <label class="form-label" for="office-name">{{ localize("R2 플래너 명") }}
          <span class="required">*</span>
        </label>
        <div class="input-group">
          <input v-model="form.plannerName" type="text" id="office-name" class="form-control curved" 
            :class="{ error: errors.plannerName }"
            :placeholder="localize('R2 플래너 명')"
          />
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="tel">{{ localize("전화번호") }}
          <span class="required">*</span>
        </label>
        <div class="input-group">
          <p class="country-code">+{{ phoneCode }}</p>
          <input
            type="tel"
            id="tel"
            class="form-control curved"
            v-model="form.phone"
            v-maska
            data-maska="#"
            data-maska-tokens="#:[0-9]:repeated"
            :placeholder="localize('숫자만 입력')"
            :class="{ error: errors.phone }"
          />
        </div>
      </div>

      <!-- 대한민국 전용-->
      <span v-if="isKorean">
        <div class="form-group">
          <label class="form-label" for="office-address">{{ localize("주소") }}
            <span class="required">*</span>
          </label>
          <div>
            <label for="postalCode" class="detail mb-1">{{ localize('우편번호') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.postalCode"
                type="text"
                id="postalCode"
                class="form-control curved"
                :placeholder="localize('우편번호')"
                :class="{ error: errors.postalCode }"
              />
            </div>
          </div>
          <div class="mb-4">
            <label for="address1" class="detail mb-1">{{ localize('주소') }}
              <span class="required">*</span>
            </label>
            <div class="input-group mt-1">
              <input
                v-model="form.address1"
                type="text"
                id="address1"
                class="form-control curved"
                :placeholder="localize('주소 입력')"
                :class="{ error: errors.address1 }"
              />
            </div>
            <div class="input-group mt-2">
              <input
                v-model="form.address2"
                type="text"
                id="address2"
                class="form-control curved"
                :placeholder="localize('상세 주소 입력')"
                :class="{ error: errors.address2 }"
              />
            </div>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label upload" for="office-name">{{ localize("자격 서류(ex 자격증 또는 관련 경력증명)") }}
            <span class="required">*</span>
            <p class="extension">{{ localize("JPG/JPEG, PNG, PDF, 40mb 이하") }}</p>
          </label>
          <div class="input-group">
            <button type="button" class="form-control curved upload-btn" @click="onClickUpload" :class="{ error: errors.plannerQualificationDocuments }">
                +
            </button>
            <input 
              type="file" 
              ref="fileInputRef" 
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              style="display: none"
              @change="onFileChange"
            />
          </div>
          <!-- 업로드 파일 목록 -->
          <div
            v-if="form.plannerQualificationDocuments.length"
            class="input-group upload-file-list"
          >
            <div
              v-for="file in form.plannerQualificationDocuments"
              :key="file.id"
              class="upload-file-item"
            >
              {{ file.name }} ({{ formatFileSize(file.size) }})
              <button type="button" class="delete-button" @click="removeUploadedFile(file.id)">x</button>
            </div>
          </div>
        </div>
      </span>
      <!-- //대한민국 전용-->

      <!-- 해외 전용-->
      <span v-else>
        <div class="form-group">
          <label class="form-label" for="office-address">{{ localize("오피스 주소") }}
            <span class="required">*</span>
          </label>
          <div class="mb-4">
            <div class="input-group mt-1">
              <input
                v-model="form.address1"
                type="text"
                id="address1"
                class="form-control curved"
                placeholder="Address 1"
                :class="{ error: errors.address1 }"
              />
            </div>
            <div class="input-group mt-2">
              <input
                v-model="form.address2"
                type="text"
                id="address2"
                class="form-control curved"
                placeholder="Address 2"
                :class="{ error: errors.address2 }"
              />
            </div>
          </div>
          <div class="mb-4">
            <label class="form-label" for="office-address">{{ localize('도시') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.city"
                type="text"
                id="city"
                class="form-control curved"
                placeholder="City"
                :class="{ error: errors.city }"
              />
            </div>
          </div>
          <div class="mb-4">
            <label class="form-label" for="office-address">{{ localize('주 / 지역') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.state"
                type="text"
                id="state-region"
                class="form-control curved"
                placeholder="State / Region"
                :class="{ error: errors.state }"
              />
            </div>
          </div>
          <div>
            <label class="form-label" for="office-address">{{ localize('우편번호') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.postalCode"
                type="text"
                id="postalCode"
                class="form-control curved"
                placeholder="Zip Code"
                :class="{ error: errors.postalCode }"
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label upload" for="office-name">{{ localize("자격 서류(ex 자격증 또는 관련 경력증명)") }}
              <span class="required">*</span>
              <p class="extension">{{ localize("JPG/JPEG, PNG, PDF, 40mb 이하") }}</p>
            </label>
            <div class="input-group">
              <button type="button" class="form-control curved upload-btn" @click="onClickUpload" :class="{ error: errors.plannerQualificationDocuments }">
                  +
              </button>
              <input 
                type="file" 
                ref="fileInputRef" 
                multiple
                accept=".jpg,.jpeg,.png,.pdf"
                style="display: none"
                @change="onFileChange"
              />
            </div>
            <!-- 업로드 파일 목록 -->
            <div
              v-if="form.plannerQualificationDocuments.length"
              class="input-group upload-file-list"
            >
              <div
                v-for="file in form.plannerQualificationDocuments"
                :key="file.id"
                class="upload-file-item"
              >
                {{ file.name }} ({{ formatFileSize(file.size) }})
                <button type="button" class="delete-button" @click="removeUploadedFile(file.id)">x</button>
              </div>
            </div>
          </div>
        </div>
      </span>
      <!-- //해외 전용-->
    </form>
    <div class="btn-wrap">
      <a :class="{ disabled: invalid() }" @click="done" class="btn btn-primary center w-100">{{
        localize("완료하기")
      }}</a>
    </div>
  </div>
  <!--  //주소 검색 팝업창-->
</template>

<style scoped lang="scss">
.form-label {
  .required {
    color: red;
    margin-left: 3px;
  }
  .extension {
    font-size: 13px;
    font-weight: 300;
  }
}
.form-label.upload {
  margin-top: 12px;
}
.input-group {
  .form-control.curved.upload-btn {
    font-weight: 700;
    color: #000000;
    height: 52px;
  }
  .form-control.curved.upload-btn.error {
    border: 1px solid #ef4444;
    padding: 0;
  }
}
.upload-file-list {
  margin-top: 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 5px;
  .delete-button {
    outline: none;
    font-weight: 700;
    color: #000000;  
  }
}

.upload-file-item {
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 14px;
  color: #555;
  word-break: break-all;
  height: 31px;
  padding: 0 16px;
  box-sizing: border-box;
  justify-content: space-between;
  border-radius: 27px;
  border: 1px solid #A3A3A3;
}
.form-control.error {
  border: 1px solid #ef4444 !important;
}
</style>
